'use client'

import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import posthog from 'posthog-js'
import {
  ExternalLink,
  UserPlus,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getPrimaryCta,
  resultsMatchIdFor,
  type Tournament,
  type CtaKind,
} from '@/lib/tournaments'
import { Button } from '@/components/ui/button'
import { InviteModal } from './invite-modal'

/** Maps the CTA kind to whether it links out and which icon to use. */
export function ctaFor(
  t: Tournament,
  opts?: { hasBracket?: boolean }
): {
  kind: CtaKind
  label: string
  href?: string
  external: boolean
  /** href points to an in-app route (render with next/link, no new tab). */
  internal?: boolean
} {
  const cta = getPrimaryCta(t)
  if (cta.kind === 'external')
    return { ...cta, href: t.externalRegistrationUrl ?? undefined, external: true }
  if (cta.kind === 'results') {
    // Hosted content wins over the external bracket link: results page first,
    // then our own bracket page, then t.bracketUrl as the external fallback.
    if (resultsMatchIdFor(t))
      return { ...cta, href: `/tournaments/${t.slug}/results`, external: false, internal: true }
    if (opts?.hasBracket)
      return {
        ...cta,
        label: 'View Bracket',
        href: `/tournaments/${t.slug}/bracket`,
        external: false,
        internal: true,
      }
    return { ...cta, href: t.bracketUrl ?? undefined, external: !!t.bracketUrl }
  }
  return { ...cta, external: false }
}

/**
 * Records who clicked through to an externally-hosted tournament (Challengermode,
 * Battlefy, organizer page…). The user is already identified via posthog.identify()
 * in AuthContext, so this captures the "who" automatically; we attach the tournament
 * context and where on the page the click happened.
 */
export function trackExternalTournamentClick(
  t: Tournament,
  placement: 'primary_cta' | 'join_button' | 'organizer_page' | 'list_card',
  href?: string | null
) {
  posthog.capture('tournament_external_link_clicked', {
    tournament_id: t.id,
    tournament_slug: t.slug,
    tournament_title: t.title,
    game: t.game,
    source: t.source ?? null,
    placement,
    url: href ?? t.externalRegistrationUrl ?? null,
  })
}

/* ---------------------------------------------------------------- */
/* Small reusable presentational pieces                              */
/* ---------------------------------------------------------------- */

export function StatTile({
  icon: Icon,
  label,
  value,
  highlight,
  className,
}: {
  icon: LucideIcon
  label: string
  value: React.ReactNode
  highlight?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4',
        className
      )}
    >
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 text-lg font-bold',
          highlight ? 'text-primary' : 'text-foreground'
        )}
      >
        {value}
      </div>
    </div>
  )
}

export function Section({
  title,
  children,
  className,
}: {
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn('', className)}>
      <h2 className="mb-3 text-lg font-semibold">{title}</h2>
      {children}
    </section>
  )
}

export function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
          <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  )
}

/* ---------------------------------------------------------------- */
/* Secondary action buttons (save / share / report)                 */
/* ---------------------------------------------------------------- */

export function SecondaryActions({
  t,
  variant = 'pill',
}: {
  /** The event being invited to — drives the invite modal's copy and link. */
  t: Tournament
  variant?: 'pill' | 'ghost'
}) {
  const [inviteOpen, setInviteOpen] = useState(false)

  const base =
    variant === 'pill'
      ? 'inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm transition-colors hover:border-primary/50'
      : 'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground'

  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={() => setInviteOpen(true)} className={base}>
        <UserPlus className="size-4" /> Invite a Friend
      </button>
      <InviteModal t={t} open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  )
}

/** Renders the status-aware primary CTA as a button or link. */
export function PrimaryCta({
  t,
  size = 'lg',
  className,
}: {
  t: Tournament
  size?: 'default' | 'sm' | 'lg'
  className?: string
}) {
  const cta = ctaFor(t)
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'

  if (cta.href && cta.internal) {
    return (
      <Button asChild size={size} className={className}>
        <Link href={`/${locale}${cta.href}`}>{cta.label}</Link>
      </Button>
    )
  }
  if (cta.href) {
    return (
      <Button asChild size={size} className={className}>
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackExternalTournamentClick(t, 'primary_cta', cta.href)}
        >
          {cta.label}
          {cta.external && <ExternalLink className="size-4" />}
        </a>
      </Button>
    )
  }
  return (
    <Button size={size} className={className}>
      {cta.label}
    </Button>
  )
}
